import { User } from '@utils/backend-api/currentUser';
import { BillingErrorLike, CtaType, getCategoryForCode, getFlowBucket } from '@utils/billingErrors';
import { triggerEventToAhoy } from '@utils/tracking';

import {
  ClickTarget,
  ImpressionElement,
  TrackingLocation,
  click,
  fireEvent,
  impression,
} from './index';

const AHOY_IMPRESSION_EVENT = 'billing_error_impression';
const AHOY_CLICK_EVENT = 'billing_error_click';

// Long enough to collapse a re-render storm, short enough that a genuine second
// decline still reports as a second impression.
const IMPRESSION_DEDUPE_MS = 1000;

const lastImpressionAt = new Map<string, number>();

// window.sendEventToAhoy is installed by window_setup.js, which does not run on
// every page the billing errors render on. Reporting must never break the error
// the user is trying to read.
function sendToAhoy(eventName: string, properties: object): void {
  if (typeof window === 'undefined' || typeof window.sendEventToAhoy !== 'function') return;
  triggerEventToAhoy(eventName, properties);
}

function billingErrorProperties(error: BillingErrorLike) {
  const errorCode = error.error_code ?? 'billing_unknown_error';

  return {
    billingFlow: getFlowBucket(error.flow),
    billingFlowDetail: error.flow ?? 'unknown',
    errorCategory: error.error_category ?? getCategoryForCode(errorCode),
    errorCode,
    recoverable: error.recoverable ?? null,
    stripeCode: error.stripe_code ?? null,
    stripeDeclineCode: error.stripe_decline_code ?? null,
  };
}

// billingErrorToast is called from plain functions as well as components, so the
// caller can't always reach useAppContext. The global is set by
// app/views/layouts/shared/_current_user.html.erb.
function resolveCurrentUser(user?: User): User | undefined {
  if (user) return user;
  if (typeof window === 'undefined') return undefined;
  return typeof currentUser === 'undefined' ? undefined : currentUser;
}

export function trackBillingErrorImpression(error: BillingErrorLike, user?: User): void {
  const properties = billingErrorProperties(error);

  const key = `${properties.errorCode}:${properties.billingFlowDetail}`;
  const now = Date.now();
  const previous = lastImpressionAt.get(key);
  if (previous !== undefined && now - previous < IMPRESSION_DEDUPE_MS) return;
  lastImpressionAt.set(key, now);

  fireEvent({
    ...impression({ ...properties, impressionElement: ImpressionElement.BillingError }),
    location: TrackingLocation.PaymentError,
    currentUser: resolveCurrentUser(user),
  });
  sendToAhoy(AHOY_IMPRESSION_EVENT, properties);
}

export function trackBillingErrorCta(
  error: BillingErrorLike,
  ctaType: CtaType,
  clickText: string,
  user?: User
): void {
  const properties = billingErrorProperties(error);
  const clickTarget =
    ctaType === 'contact_support' ? ClickTarget.ContactSupport : ClickTarget.BillingErrorCta;
  const clickLink = error.cta_link ?? undefined;

  fireEvent({
    ...click({ ...properties, clickTarget, clickText, clickLink }),
    location: TrackingLocation.PaymentError,
    currentUser: resolveCurrentUser(user),
  });
  sendToAhoy(AHOY_CLICK_EVENT, {
    ...properties,
    cta_type: ctaType,
    click_text: clickText,
    click_link: clickLink ?? null,
  });
}
